import usePersistentInputState from '../../views/chat-view/usePersistentInputState';
import usePersistentAutoSubmitState from '../../views/chat-view/usePersistentLoadingForState';
import { PropsWithChildren, createContext, useContext } from 'react';
import { GetChatResponse, SessionResponse, ChatMetadata, ChatMessageStream, ChatMessageTracesResponse, DocumentInfoModel, DocumentUpdateRequest, DocumentsResponse, DeleteDocumentsResponse, DocumentArtifactsUrlRequest } from '../../models/ChatTypes';
import { ChatMessageFeedback, ProductivityGainResponse, UpdateChatMetadataResponse, UserPreferences } from '../../models/types';
import { IChatService } from '../../services/IChatService';
import { ActionResult, ComponentsData, FormAction, TableRowsResponse } from '../../models/SidePanelTypes';
import { NotImplementedEventHandler } from '../../services/EventHandler';
import useFileUploadQueue from '../../views/chat-view/useFileUploadQueue';

export type ExtendedIChatService = IChatService & {
    persistent: ReturnType<typeof usePersistentInputState> & ReturnType<typeof usePersistentAutoSubmitState> & ReturnType<typeof useFileUploadQueue>;
};

const notImplemented = () => {
    throw new Error('Function not implemented.');
};
const defaultChatService: IChatService = {
    getChat: function (sessionId: string): Promise<GetChatResponse> {
        return notImplemented();
    },
    getAllChats: function (): Promise<SessionResponse[]> {
        return notImplemented();
    },
    createChat: function (initialMessage: string, llm: string, temperature: number, persona?: string, version?: string, build?: number, documents?: string[], data?: Record<string, any>, extendsSessionId?: string): Promise<SessionResponse> {
        return notImplemented();
    },
    updateChatMetadata: function (sessionId: string, metadata: Partial<ChatMetadata>): Promise<UpdateChatMetadataResponse> {
        return notImplemented();
    },
    setSessionCurrentPersonaVersion: function (sessionId: string, version: string, build: number): Promise<SessionResponse> {
        return notImplemented();
    },
    uploadDocument: function (): Promise<DocumentInfoModel> {
        return notImplemented();
    },
    getDocuments: function (): Promise<DocumentsResponse> {
        return notImplemented();
    },
    downloadDocuments: function (props: { fileIds: string[]; }): Promise<boolean> {
        return notImplemented();
    },
    getDocumentArtifactUrl: function (props: DocumentArtifactsUrlRequest): Promise<string> {
        return notImplemented();
    },
    fetchDocuments: function (props: { fileIds: string[], sessionId?: string; }): Promise<DocumentsResponse> {
        return notImplemented();
    },
    copyDocuments: function (props: { document_ids: string[]; }): Promise<DocumentsResponse> {
        return notImplemented();
    },
    updateDocumentMetadata: function (props: { fileId: string, metadata: DocumentUpdateRequest; }): Promise<DocumentInfoModel> {
        return notImplemented();
    },
    deleteDocuments: function (props: { fileIds: string[], sessionId: string; }): Promise<DeleteDocumentsResponse> {
        return notImplemented();
    },
    deleteChat: function (sessionId: string): Promise<void> {
        return notImplemented();
    },
    deleteAllChats: function (): Promise<void> {
        return notImplemented();
    },
    prompt: function (message: string, conversationId: string, promptTemplateId?: string): Promise<ChatMessageStream> {
        return notImplemented();
    },
    dataSidePanel: function (sessionId: string, objectId: string): Promise<ComponentsData> {
        return notImplemented();
    },
    dataSidePanelAction: function <F extends FormAction>(sessionId: string, formAction: F, data?: Record<string, any>): Promise<ActionResult<F>> {
        return notImplemented();
    },

    setUserPreferences: function (listPatchMode: 'replace' | 'add-items' | 'remove-items', preferences: Partial<UserPreferences>): Promise<UserPreferences> {
        return notImplemented();
    },

    getUserPreferences: function (): Promise<UserPreferences> {
        return notImplemented();
    },

    tableData: function (sessionId: string, source: string, page?: number, params?: Record<string, string[]>): Promise<TableRowsResponse> {
        return notImplemented();
    },
    messageLike: function (sessionId: string, messageId: string): Promise<void> {
        return notImplemented();
    },
    messageDislike: function (sessionId: string, messageId: string): Promise<void> {
        return notImplemented();
    },
    messageComment: function (sessionId: string, messageId: string, value: string): Promise<void> {
        return notImplemented();
    },
    getChatMessagesFeedback: function (sessionId: string): Promise<ChatMessageFeedback[]> {
        return notImplemented();
    },
    getMessageFeedback: function (sessionId: string, messageId: string): Promise<ChatMessageFeedback> {
        return notImplemented();
    },
    messageResetVote: function (sessionId: string, messageId: string): Promise<void> {
        return notImplemented();
    },
    messageInsights: function (traceId: string): Promise<ChatMessageTracesResponse> {
        return notImplemented();
    },
    submitProductivityGain: function (sessionId: string, value: number): Promise<ProductivityGainResponse> {
        return notImplemented();
    },

    events: {
        session: new NotImplementedEventHandler(),
        document: new NotImplementedEventHandler()
    }
};

export const ChatContext = createContext<IChatService>(defaultChatService);

const ChatProvider = (props: PropsWithChildren<ExtendedIChatService>) => {
    return (
        <ChatContext.Provider value={props}>
            {props.children}
        </ChatContext.Provider>
    );
};


export const useChat = () => useContext(ChatContext) as ExtendedIChatService;

export default ChatProvider;
